import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Barbell, Timer, Heart, Flame } from "@phosphor-icons/react";
import { metricStats, trendGood } from "./metricDefs";
import { TrendUp, TrendDown } from "@phosphor-icons/react";

const monthsRu = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];
const fmtFull = (d) => `${parseInt(d.slice(8, 10), 10)} ${monthsRu[parseInt(d.slice(5, 7), 10) - 1]} ${d.slice(0, 4)}`;
const recZone = (r) => (r == null ? null : r >= 67
  ? { color: "#00e676", label: "Зелёная зона", text: "Организм был готов к высокой нагрузке." }
  : r >= 34
  ? { color: "#ffea00", label: "Жёлтая зона", text: "Организм справлялся, но не на пике." }
  : { color: "#ff3b30", label: "Красная зона", text: "Телу требовался отдых." });

function Stat({ label, value, unit, delta, deltaDir }) {
  return (
    <div className="rounded-md bg-secondary/60 p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      <div className="num text-sm font-semibold flex items-center gap-1.5">
        {value != null ? `${value}${unit ? ` ${unit}` : ""}` : "—"}
        {delta != null && Math.abs(delta) >= 3 && (
          <span className={`flex items-center text-[10px] ${deltaDir === "good" ? "text-[#00e676]" : deltaDir === "bad" ? "text-[#ff3b30]" : "text-muted-foreground"}`}>
            {delta > 0 ? <TrendUp size={11} /> : <TrendDown size={11} />}{Math.abs(delta).toFixed(0)}%
          </span>
        )}
      </div>
    </div>
  );
}

export default function DayDetailSheet({ date, days, workouts, onClose }) {
  const day = date ? days.find((d) => d.date === date) : null;
  const dayWorkouts = date ? workouts.filter((w) => w.date === date) : [];
  const zone = day ? recZone(day.recovery) : null;

  const deltaOf = (key) => {
    if (!day || day[key] == null) return [null, "neutral"];
    const s = metricStats(days, key);
    if (!s || !s.avg) return [null, "neutral"];
    const pct = ((day[key] - s.avg) / Math.abs(s.avg)) * 100;
    return [pct, trendGood(key, pct)];
  };

  const stat = (label, key, unit) => {
    const [delta, dir] = deltaOf(key);
    return <Stat label={label} value={day?.[key]} unit={unit} delta={delta} deltaDir={dir} />;
  };

  const sleepTotal = day ? (day.sleep_h || 0) + (day.awake_h || 0) : 0;

  return (
    <Sheet open={!!date} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="glass-modal w-full sm:max-w-xl overflow-y-auto" data-testid="day-detail-sheet">
        {date && (
          <>
            <SheetHeader>
              <SheetTitle className="font-heading text-2xl">{fmtFull(date)}</SheetTitle>
              <SheetDescription className="text-muted-foreground">Все данные дня · стрелки — отклонение от вашей базы</SheetDescription>
            </SheetHeader>
            {!day ? (
              <p className="mt-6 text-sm text-muted-foreground">Нет данных за этот день</p>
            ) : (
              <div className="mt-4 space-y-5">
                {zone && (
                  <div className="rounded-lg border p-4" style={{ borderColor: `${zone.color}40`, background: `${zone.color}0d` }} data-testid="day-verdict">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: zone.color }} />
                      <span className="font-semibold text-sm">Восстановление {Math.round(day.recovery)}% — {zone.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{zone.text}</p>
                  </div>
                )}
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Восстановление</div>
                  <div className="grid grid-cols-3 gap-2">
                    {stat("HRV", "hrv", "мс")}
                    {stat("Пульс покоя", "rhr", "уд/мин")}
                    {stat("SpO₂", "spo2", "%")}
                    {stat("Темп. кожи", "skin_temp", "°C")}
                    {stat("Дыхание", "resp_rate", "вд/мин")}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Сон</div>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {stat("Длительность", "sleep_h", "ч")}
                    {stat("Эффективность", "sleep_performance", "%")}
                    {stat("Стабильность", "sleep_consistency", "%")}
                    {stat("Качество", "sleep_efficiency", "%")}
                    <Stat label="Пробуждения" value={day.disturbances} />
                  </div>
                  {sleepTotal > 0 && (
                    <>
                      <div className="flex h-3 rounded-full overflow-hidden gap-px" data-testid="day-sleep-phases">
                        {[["deep_h", "#3d5afe"], ["rem_h", "#a970ff"], ["light_h", "#4dabf5"], ["awake_h", "hsl(220 10% 30%)"]].map(([k, c]) =>
                          (day[k] || 0) > 0 && <div key={k} style={{ width: `${(day[k] / sleepTotal) * 100}%`, background: c }} />
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-muted-foreground">
                        <span><span className="inline-block w-2 h-2 rounded-full bg-[#3d5afe] mr-1" />Глубокий {day.deep_h} ч</span>
                        <span><span className="inline-block w-2 h-2 rounded-full bg-[#a970ff] mr-1" />REM {day.rem_h} ч</span>
                        <span><span className="inline-block w-2 h-2 rounded-full bg-[#4dabf5] mr-1" />Лёгкий {day.light_h} ч</span>
                        <span><span className="inline-block w-2 h-2 rounded-full bg-[hsl(220,10%,45%)] mr-1" />Бодрств. {day.awake_h} ч</span>
                      </div>
                    </>
                  )}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Нагрузка</div>
                  <div className="grid grid-cols-3 gap-2">
                    {stat("Нагрузка", "strain", "")}
                    {stat("Калории", "kcal", "ккал")}
                    <Stat label="Пульс ср./макс." value={day.avg_hr != null ? `${day.avg_hr}/${day.max_hr}` : null} />
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Тренировки · {dayWorkouts.length}</div>
                  {dayWorkouts.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Не было тренировок</p>
                  ) : (
                    <div className="space-y-2">
                      {dayWorkouts.map((w) => (
                        <div key={w.id} className="rounded-md bg-secondary/60 p-3 flex items-center gap-3 text-xs">
                          <Barbell size={16} weight="duotone" className="text-primary shrink-0" />
                          <span className="font-semibold flex-1">{w.sport}</span>
                          <span className="flex items-center gap-1 text-muted-foreground"><Timer size={12} /><span className="num">{w.duration_min} мин</span></span>
                          <span className="flex items-center gap-1 text-muted-foreground"><Heart size={12} /><span className="num">{w.avg_hr ?? "—"}</span></span>
                          <span className="flex items-center gap-1 text-muted-foreground"><Flame size={12} /><span className="num">{w.kcal ?? "—"}</span></span>
                          <span className="num font-bold text-primary">{w.strain}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
